import React, { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';

const projects = [
  {
    title: 'AI-Powered Analytics Platform',
    category: 'Generative AI + Web',
    tags: ['Next.js', 'OpenAI', 'RAG', 'AWS'],
    desc: 'Built a real-time analytics dashboard with natural language querying — business users ask questions, the system answers with charts and insights.',
    outcome: '70% reduction in BI reporting time',
    color: '#258ACB',
    gradient: 'linear-gradient(135deg, rgba(37,138,203,0.2) 0%, rgba(11,17,32,0) 100%)',
  },
  {
    title: 'Multi-Cloud DevOps Platform',
    category: 'DevOps & Cloud',
    tags: ['AWS', 'Azure', 'Kubernetes', 'Terraform'],
    desc: 'Designed and deployed a multi-cloud orchestration system for a fintech company, enabling zero-downtime deployments across 3 cloud providers.',
    outcome: '99.99% uptime, 3x deploy speed',
    color: '#3BA3E8',
    gradient: 'linear-gradient(135deg, rgba(59,163,232,0.2) 0%, rgba(11,17,32,0) 100%)',
  },
  {
    title: 'Agentic Customer Support System',
    category: 'Agentic AI',
    tags: ['LangChain', 'Python', 'React', 'MongoDB'],
    desc: 'Autonomous AI agents that handle 85% of customer support tickets — triage, respond, escalate, and learn from every interaction.',
    outcome: '85% ticket auto-resolution rate',
    color: '#1A6FA8',
    gradient: 'linear-gradient(135deg, rgba(26,111,168,0.2) 0%, rgba(11,17,32,0) 100%)',
  },
  {
    title: 'Cross-Platform Healthcare App',
    category: 'Mobile Development',
    tags: ['Flutter', 'Firebase', 'Node.js', 'HL7'],
    desc: 'HIPAA-compliant telemedicine app connecting patients to doctors with real-time video, e-prescriptions, and health record management.',
    outcome: '50K+ downloads in 6 months',
    color: '#258ACB',
    gradient: 'linear-gradient(135deg, rgba(37,138,203,0.2) 0%, rgba(11,17,32,0) 100%)',
  },
  {
    title: 'Enterprise E-Commerce Platform',
    category: 'Web Development',
    tags: ['Next.js', 'Node.js', 'PostgreSQL', 'Redis'],
    desc: 'Migrated a legacy PHP monolith to a modern microservices architecture with 10x performance improvement and global CDN delivery.',
    outcome: '10x performance, $2M+ revenue impact',
    color: '#3BA3E8',
    gradient: 'linear-gradient(135deg, rgba(59,163,232,0.2) 0%, rgba(11,17,32,0) 100%)',
  },
  {
    title: 'LLM Document Intelligence',
    category: 'Generative AI',
    tags: ['OpenAI', 'LangChain', 'Python', 'Vector DB'],
    desc: 'RAG-based document analysis system that processes thousands of legal documents per hour — extracting clauses, risks, and summaries automatically.',
    outcome: '90% faster document review',
    color: '#1A6FA8',
    gradient: 'linear-gradient(135deg, rgba(26,111,168,0.2) 0%, rgba(11,17,32,0) 100%)',
  },
];

export default function Portfolio() {
  const [hovered, setHovered] = useState(null);

  return (
    <section id="portfolio" style={{ padding: '7rem 0', position: 'relative', overflow: 'hidden' }}>
      <div className="glow-orb" style={{
        width: '600px', height: '600px',
        background: 'radial-gradient(circle, rgba(37,138,203,0.07) 0%, transparent 70%)',
        top: '50%', right: '-100px', transform: 'translateY(-50%)',
      }} />

      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div className="section-label" style={{ margin: '0 auto 1.5rem' }}>
            <span>◆</span> Case Studies
          </div>
          <h2 className="section-heading" style={{ margin: '0 auto 1rem' }}>
            Real work, <span className="gradient-text">real results</span>
          </h2>
          <p className="section-subtext" style={{ margin: '0 auto' }}>
            A sample of the projects that define what we're capable of building.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: '1.5rem',
        }}>
          {projects.map((p, i) => (
            <div
              key={p.title}
              className="glass-card"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                background: hovered === i ? p.gradient : 'var(--glass)',
                cursor: 'pointer',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <span style={{
                  fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: p.color,
                  background: `rgba(37,138,203,0.08)`, border: '1px solid rgba(37,138,203,0.2)',
                  borderRadius: '4px', padding: '0.2rem 0.6rem',
                }}>{p.category}</span>
                <ArrowUpRight size={18} style={{
                  color: hovered === i ? p.color : 'var(--text-muted)',
                  transition: 'color 0.3s, transform 0.3s',
                  transform: hovered === i ? 'translate(2px,-2px)' : 'none',
                }} />
              </div>

              <h3 style={{
                fontFamily: 'var(--font-display)', fontSize: '1.15rem', fontWeight: 700,
                marginBottom: '0.6rem', lineHeight: 1.3,
              }}>{p.title}</h3>

              <p style={{
                fontSize: '0.88rem', color: 'var(--text-secondary)',
                lineHeight: 1.65, marginBottom: '1.25rem',
              }}>{p.desc}</p>

              {/* Outcome */}
              <div style={{
                background: 'rgba(37,138,203,0.08)', border: '1px solid rgba(37,138,203,0.2)',
                borderRadius: '8px', padding: '0.75rem 1rem',
                fontSize: '0.85rem', fontWeight: 600, color: p.color,
                marginBottom: '1.25rem',
              }}>
                📈 {p.outcome}
              </div>

              {/* Tags */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {p.tags.map(tag => (
                  <span key={tag} style={{
                    fontSize: '0.73rem', fontWeight: 500,
                    color: 'var(--text-muted)', background: 'rgba(37,138,203,0.04)',
                    border: '1px solid var(--border)', borderRadius: '4px',
                    padding: '0.2rem 0.55rem',
                  }}>{tag}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <a href="#contact" className="btn-ghost">
            Discuss Your Project →
          </a>
        </div>
      </div>
    </section>
  );
}
